import { cn } from "@/lib/utils";
import { StreakDropdown } from "components/core/StreakDropdown";
import { useEffect, useRef, useState } from "react";

type Props = {
    streak: number;
    className?: string;
    // "compact" = 헤더용 숫자만, "full" = 마이페이지용 레이블 포함
    variant?: "compact" | "full";
    // 드롭다운에 필요한 추가 데이터
    longestStreak?: number;
    recentDays?: string[];
    readDatesSet?: Set<string>;
    totalReadDays?: number;
    today?: string;
};

/**
 * 🔥 Daily reading streak 뱃지 컴포넌트.
 * - compact: 헤더에 프로필 옆에 표시 (🔥 7) + 호버 드롭다운
 * - full: 마이페이지 정보 섹션에 표시 (🔥 7일 연속)
 */
export const StreakBadge = ({
    streak,
    className,
    variant = "compact",
    longestStreak = 0,
    recentDays = [],
    readDatesSet = new Set(),
    totalReadDays = 0,
    today = ""
}: Props) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // 외부 클릭 시 드롭다운 닫기
    useEffect(() => {
        if (!open) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () =>
            document.removeEventListener("mousedown", handleOutsideClick);
    }, [open]);

    // 스트릭이 0이면 표시하지 않음
    if (streak <= 0) return null;

    const hasDropdownData = recentDays.length > 0;

    if (variant === "full") {
        return (
            <span
                className={cn(
                    "inline-flex items-center gap-1 text-base font-medium",
                    className
                )}
            >
                🔥
                <span className="text-orange-500">{streak}일 연속</span>
            </span>
        );
    }

    return (
        <div className="relative [@media(max-width:320px)]:-mr-2" ref={ref}>
            <span
                className={cn(
                    `inline-flex w-[40px] cursor-pointer items-center gap-0.5
                    rounded-full`,
                    `bg-orange-50 px-1.5 py-0.5 text-xs font-semibold
                    text-orange-500`,
                    className
                )}
                onClick={() => setOpen((prev) => !prev)}
            >
                🔥 {streak}
            </span>

            {/* 클릭 드롭다운 — 뱃지 아래 오른쪽 정렬 (모든 화면) */}
            {open && hasDropdownData && (
                <div className="absolute right-0 top-full z-[200] pt-2">
                    <StreakDropdown
                        currentStreak={streak}
                        longestStreak={longestStreak}
                        recentDays={recentDays}
                        readDatesSet={readDatesSet}
                        totalReadDays={totalReadDays}
                        today={today}
                    />
                </div>
            )}
        </div>
    );
};
